import { Vector3 } from 'three';
import { CamelCasedProperties } from 'type-fest';

import { isValidNumber } from '../../../utils/math/number';
import { isDefined } from '../../../utils/variables';
import { Camera } from './camera';
import { AvarageError, GCP } from './gcp';
import { Marker } from './marker';

type GcpErrorXml = {
  camera_label: string;
  gcp_label: string;
  x: number;
  y: number;
  z: number;
  reprojection_error: number;
};

export type GcpError = CamelCasedProperties<GcpErrorXml>;

function findChild(parent: Element, tag: string): Element | undefined {
  return Array.from(parent.children).find((e) => e.tagName === tag);
}

function calculateAvarageError(errors: GcpError[]): AvarageError | undefined {
  if (errors.length === 0) {
    return undefined;
  }

  const result: Vector3 = errors.reduce((prev, next) => {
    prev.x += Math.abs(next.x);
    prev.y += Math.abs(next.y);
    prev.z += Math.abs(next.z);
    return prev;
  }, new Vector3());

  result.divideScalar(errors.length);

  return {
    x: result.x,
    y: result.y,
    z: result.z,
    total: result.length(),
  };
}

function parseGcpError(element: Element): GcpError | undefined {
  const cameraLabel = element.getAttribute('camera_label') ?? undefined;
  const gcpLabel = element.getAttribute('gcp_label') ?? undefined;

  const values = [
    Number.parseFloat(element.getAttribute('x') ?? ''),
    Number.parseFloat(element.getAttribute('y') ?? ''),
    Number.parseFloat(element.getAttribute('z') ?? ''),
    Number.parseFloat(element.getAttribute('reprojection_error') ?? ''),
  ];

  if (!values.every(isValidNumber) || !isDefined(cameraLabel) || !isDefined(gcpLabel)) {
    return undefined;
  }

  return {
    cameraLabel,
    gcpLabel,
    x: values[0],
    y: values[1],
    z: values[2],
    reprojectionError: values[3],
  };
}

export class GcpXmlParser {
  cameras: Camera[] = [];
  gcpPoints: GCP[] = [];
  markers: Marker[] = [];
  chunkTransform?: {
    rotation9: number[];
    translation3: number[];
    scale: number;
  };

  private doc: Document;
  private chunkElement: Element;
  private sensorElements: Element[];
  private cameraElements: Element[];
  private gcpErrors: GcpError[] = [];

  private gcpElements?: Element[];
  private markerElements?: Element[];
  private errorsElement?: Element;

  constructor(public xml: string) {
    this.doc = new DOMParser().parseFromString(this.xml, 'text/xml');

    this.chunkElement = this.doc.documentElement.children[0];
    this.errorsElement = this.doc.documentElement.children.item(1) ?? undefined;

    this.sensorElements = this.extractChildElements(this.chunkElement, 'sensors');
    this.cameraElements = this.extractChildElements(this.chunkElement, 'cameras');
    this.chunkTransform = this.extractChunkTransform(this.chunkElement);

    this.gcpElements = this.parseGcpElements();
    this.markerElements = this.parseMarkerElements();

    this.gcpErrors = this.parseGcpErrors();
    this.generateCameras();
    this.generateGcpPoints();
    this.generateMarkers();
  }

  getXmlData(): string {
    this.saveGcpPointsAndMarkers();
    return new XMLSerializer().serializeToString(this.doc);
  }

  extractChunkTransform(chunkElement: Element):
    | {
        rotation9: number[];
        translation3: number[];
        scale: number;
      }
    | undefined {
    const transform = findChild(chunkElement, 'transform');

    if (!transform) {
      return undefined;
    }

    const translation3El = findChild(transform, 'translation');
    const rotation9El = findChild(transform, 'rotation');
    const scaleEl = findChild(transform, 'scale');

    if (!translation3El?.textContent || !rotation9El?.textContent || !scaleEl?.textContent) {
      return undefined;
    }

    const parseNumbers = (el: Element) => el.textContent!.trim().split(' ').map(Number);

    return {
      translation3: parseNumbers(translation3El),
      rotation9: parseNumbers(rotation9El),
      scale: Number(scaleEl.textContent.trim()),
    };
  }

  extractChildElements(sourceElement: Element, tag: string): Element[] {
    const child = findChild(sourceElement, tag);
    if (!child || !child.children.length) {
      throw new Error(`Child element with tag ${tag} is missing or empty`);
    }
    return Array.from(child.children);
  }

  parseGcpElements(): Element[] | undefined {
    const markers = findChild(this.chunkElement, 'markers');
    return markers ? Array.from(markers.children) : undefined;
  }

  parseMarkerElements(): Element[] | undefined {
    const frames = findChild(this.chunkElement, 'frames');
    const frame = frames?.children[0];
    const markers = frame?.children[0];
    if (!markers) {
      return;
    }
    return Array.from(markers.children);
  }

  generateCameras() {
    for (const element of this.cameraElements) {
      const camera = Camera.fromXml(element);

      const sensor = this.sensorElements.find(
        (e) => Number(e.getAttribute('id')) === camera.sensorId,
      );
      if (!sensor) {
        throw new Error(`No sensor with id in Xml ${camera.sensorId}`);
      }

      const resolution = findChild(sensor, 'resolution');
      if (!resolution) {
        throw new Error(`Sensor is missing or contains invalid resolution`);
      }

      camera.setResolution(
        Number(resolution.getAttribute('width')),
        Number(resolution.getAttribute('height')),
      );
      this.cameras.push(camera);
    }
  }

  parseGcpErrors(): GcpError[] {
    return Array.from(this.errorsElement?.children ?? [])
      .map(parseGcpError)
      .filter(isDefined);
  }

  generateGcpPoints() {
    if (this.gcpElements) {
      for (const element of this.gcpElements) {
        const gcpPoint = GCP.createFromXml(element);
        this.gcpPoints.push(gcpPoint);

        if (this.errorsElement) {
          gcpPoint.avarageError = calculateAvarageError(
            this.gcpErrors.filter((x) => x.gcpLabel === gcpPoint.name),
          );
        }
      }
    }
  }

  generateMarkers() {
    if (this.markerElements) {
      for (const element of this.markerElements) {
        if (element.children.length) {
          const gcp = this.gcpPoints.find(
            (p: GCP) => p.id === Number(element.getAttribute('marker_id')),
          );
          for (const markerElement of Array.from(element.children)) {
            if (markerElement.tagName !== 'location') {
              continue;
            }
            const marker = Marker.fromXml(markerElement);
            marker.gcp = gcp;
            const camera = this.cameras.find((cam) => cam.id === marker.cameraId);
            if (camera) {
              camera.addMarker(marker);
              marker.camera = camera;
            }

            const error = this.gcpErrors.find(
              (x) => x.cameraLabel === marker.camera?.label && x.gcpLabel === marker.gcp?.name,
            );
            if (isDefined(error)) {
              marker.reprojectionError = error.reprojectionError;
            }
          }
        }
      }
    }
  }

  /** Saves gcpPoints [] back into the parsed document */
  saveGcpPointsAndMarkers() {
    const markersElement = this.createEl('markers', {
      next_id: String(this.gcpPoints.length),
      next_group_id: '0',
    });

    const frameMarkers = this.createEl('markers', {});
    const frame = this.createEl('frame', { id: '0' });
    frame.appendChild(frameMarkers);
    const framesElement = this.createEl('frames', { next_id: '1' });
    framesElement.appendChild(frame);

    for (let i = 0; i < this.gcpPoints.length; i++) {
      const gcpPoint = this.gcpPoints[i];
      gcpPoint.id = i;
      const reference = this.createEl('reference', {
        x: String(gcpPoint.easting),
        y: String(gcpPoint.northing),
        z: String(gcpPoint.height),
        enabled: 'true',
      });
      const marker = this.createEl('marker', { id: String(i), label: gcpPoint.name });
      marker.appendChild(reference);
      markersElement.appendChild(marker);

      frameMarkers.appendChild(this.createEl('marker', { marker_id: String(i) }));
    }

    for (const cam of this.cameras) {
      if (cam.markers && cam.markers.length > 0) {
        for (const marker of cam.markers) {
          const gcpElement = Array.from(frameMarkers.children).find(
            (e) => Number(e.getAttribute('marker_id')) === marker.gcp!.id,
          );
          gcpElement?.appendChild(this.createFlag(cam.id, marker.x, marker.y, marker.pinned));
        }
      }

      this.updateCameraEnabled(cam);
    }

    // Add <markers> tag after <cameras> or replace if already exists
    const existingMarkersElement = findChild(this.chunkElement, 'markers');
    if (existingMarkersElement) {
      this.chunkElement.replaceChild(markersElement, existingMarkersElement);
    } else {
      this.chunkElement.insertBefore(markersElement, this.chunkElement.children.item(2) ?? null);
    }

    // Add <frames> tag last or replace if already exists
    const existingFramesElement = findChild(this.chunkElement, 'frames');
    if (existingFramesElement) {
      this.chunkElement.replaceChild(framesElement, existingFramesElement);
    } else {
      this.chunkElement.appendChild(framesElement);
    }
  }

  updateCameraEnabled(camera: Camera) {
    const state = camera.enabled ? 'true' : 'false';
    const referenceState = camera.referenceEnabled ? 'true' : 'false';

    for (const element of this.cameraElements) {
      if (Number(element.getAttribute('id')) === camera.id) {
        element.setAttribute('enabled', state);
        const reference = findChild(element, 'reference');
        reference?.setAttribute('enabled', referenceState);
      }
    }
  }

  private createEl(name: string, attributes: Record<string, string>): Element {
    const el = this.doc.createElement(name);
    for (const [key, value] of Object.entries(attributes)) {
      el.setAttribute(key, value);
    }
    return el;
  }

  private createFlag(cameraId: number, x: number, y: number, pinned: boolean): Element {
    return this.createEl('location', {
      camera_id: String(cameraId),
      pinned: pinned ? 'true' : 'false',
      x: String(x),
      y: String(y),
    });
  }
}
